import {
  useStateWithSearchParams,
  useAppDispatch,
  useAppSelector,
} from "../../../hooks";
import {
  accountsApi,
  campaignsApi,
  useAnalyseAllMutation,
  useAnalyseMutation,
  useGetCampaignSummaryQuery,
} from "../../../api";
import { useCallback, useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { CustomToast } from "../CustomToast";
import { AnalysisTimeToastText } from "../AnalysisTimeToastText";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { setIsNotificationsOpen } from "features/companySlice";
import { QUERY_SEPARATOR } from "../../../constant";
import { selectPermissions } from "../../../features/permissionsSlice";
import { ButtonsColorType, ButtonsType } from "../../../types/enums";
import { ReuseButton } from "../ReuseButton";
import { AnalysisStatus } from "../../../types/companyTypes";

type SearchParams = {
  selected?: string;
};

export const AnalyzeAllBtn = ({
  campaign_id,
  onAnalysisStart,
}: {
  campaign_id: string;
  onAnalysisStart?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const analyzeStartedAt = useRef<number | null>(null);
  const completedCount = useRef<number>(0);
  const totalSelected = useRef<number>(0);

  const { isAllowedEditLaunchAccountAnalysis } =
    useAppSelector(selectPermissions);
  const { analysis_status } = useAppSelector((state) => state.company.status);

  const [analyse] = useAnalyseMutation();
  const [analyseAll, { isLoading: isAnalysingAll }] = useAnalyseAllMutation();
  const { data: summary } = useGetCampaignSummaryQuery({ campaign_id });

  useEffect(() => {
    if (isAnalysingAll) setIsLoading(true);
  }, [isAnalysingAll]);

  useEffect(() => {
    setIsLoading(summary?.analyze_all_status === "in_progress");
  }, [summary]);

  useEffect(() => {
    if (
      !analyzeStartedAt.current ||
      !totalSelected.current ||
      analysis_status?.status !== AnalysisStatus.completed
    )
      return;

    completedCount.current += 1;

    if (completedCount.current >= totalSelected.current) {
      analyzeStartedAt.current = null;
      completedCount.current = 0;
      totalSelected.current = 0;
    }
  }, [analysis_status]);

  const { searchDict, setSearchValue } = useStateWithSearchParams<SearchParams>(
    { selected: undefined },
  );

  const analyseHandler = useCallback(() => {
    const accountIds = searchDict.selected?.split(QUERY_SEPARATOR);
    analyzeStartedAt.current = Date.now();

    if (searchDict.selected) {
      totalSelected.current = accountIds?.length ?? 0;
      completedCount.current = 0;
    }

    onAnalysisStart?.();

    const handler = () => {
      setSearchValue("selected", undefined);
      dispatch(campaignsApi.util.invalidateTags(["activities", "summary"]));
      dispatch(accountsApi.util.invalidateTags(["list-view"]));
      dispatch(setIsNotificationsOpen(true));
    };

    if (searchDict.selected) {
      analyse({ account_ids: accountIds!, campaign_id })
        .unwrap()
        .then((res) => {
          if (res?.approximate_time) {
            toast.success(
              <CustomToast
                type="success"
                title="Analysis started"
                text={<AnalysisTimeToastText seconds={res.approximate_time} />}
              />,
            );
          }
          handler();
        })
        .catch((err) => {
          toast.error(
            <CustomToast
              type="error"
              title="Something went wrong during the analysis."
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again"
              }
            />,
          );
        });
    } else {
      analyseAll({ campaign_id })
        .unwrap()
        .then((res) => {
          if (res?.approximate_time) {
            toast.success(
              <CustomToast
                type="success"
                title="Analysis started"
                text={<AnalysisTimeToastText seconds={res.approximate_time} />}
              />,
            );
          }
          handler();
        })
        .catch((err) => {
          toast.error(
            <CustomToast
              type="error"
              title="Something went wrong during the analysis."
              text={
                getErrorMessage(err).globalError ||
                "Something went wrong. Please try again"
              }
            />,
          );
          setIsLoading(false);
        });
    }
  }, [searchDict.selected, campaign_id, summary, onAnalysisStart]);

  return (
    <ReuseButton
      onClick={analyseHandler}
      buttonColor={ButtonsColorType.gradient}
      buttonType={ButtonsType.contained}
      disabled={
        "not_allowed" === summary?.analyze_all_status ||
        isLoading ||
        !isAllowedEditLaunchAccountAnalysis
      }
      startIcon={
        isLoading && <CircularProgress size={24} sx={{ color: "inherit" }} />
      }
    >
      {isLoading
        ? "Analysis in Progress"
        : searchDict.selected
          ? "Analyze"
          : "Analyze Accounts"}
    </ReuseButton>
  );
};
